import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Crown, Lock, Mail, CheckCircle, XCircle, Shield, Search, User, Trash2, AlertTriangle } from 'lucide-react';
import { PollarBrandMini } from '@/components/PollarBrand';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DELETE_WORDS = ["mist","snap","brim","curl","dash","twig","bolt","hush","glow","pact",
"loop","drift","spark","tint","mesh","roam","sprig","tuck","flip","crest",
"plum","skid","clasp","mint","sway","gulp","cliff","stash","perk","loom",
"fleck","grin","snip","flap","clamp","reef","spur","nook","scrap","dusk",
"whim","damp","rant","claw","yarn","perk","moss","chip","brisk","flit",
"puff","glint","bloom","snag","trill","peel","hatch","scoop","wisp","flock",
"mirth","quill","speck","fizz","trot","crisp","hinge","flame","soot","braid",
"pluck","quilt","froth","stir","mend","silt","perk","flask","grain","pore",
"rift","crave","coil","flume","swirl","throb","clink","wade","sloop","perch",
"gleam","spurt","tend","wink","lush","crust","sprint","nudge","fend","zest"];

const Profile = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  
  // Delete Account State
  const [isDeleting, setIsDeleting] = useState(false);
  const [targetWords, setTargetWords] = useState([]);
  const [enteredWords, setEnteredWords] = useState([]);
  const [currentWordInput, setCurrentWordInput] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const inputRef = useRef(null);
  
  // Admin features
  const [searchEmail, setSearchEmail] = useState('');
  const [searchedUser, setSearchedUser] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login?signup=true');
      return;
    }
    
    fetchProfile();
  }, [navigate]);

  const generateDeletePhrase = () => {
    const shuffled = [...DELETE_WORDS].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 5);
  };

  const openDeleteModal = () => {
    setTargetWords(generateDeletePhrase());
    setEnteredWords([]);
    setCurrentWordInput('');
    setDeletePassword('');
    setIsDeleting(true);
  };

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
      
      // Update localStorage facilitator to ensure all pages get fresh data
      localStorage.setItem('facilitator', JSON.stringify(response.data));
    } catch (error) {
      toast.error('Failed to load profile');
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setChangingPassword(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/profile/password`,
        {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Password changed successfully!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/auth/resend-verification`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification email');
    } finally {
      setResendingVerification(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your premium subscription? You will lose access to premium features at the end of your billing period.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/payment/cancel-subscription`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(response.data.message || 'Subscription cancelled successfully.');
      
      // Refresh profile
      await fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/payment/reactivate-subscription`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(response.data.message || 'Subscription reactivated successfully!');
      
      // Refresh profile
      await fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate subscription');
    }
  };

  const handleDeleteAccount = async () => {
    if (enteredWords.join(' ') !== targetWords.join(' ')) {
      toast.error('Confirmation phrase does not match');
      return;
    }
    
    if (!profile.is_google_user && !deletePassword) {
      toast.error('Please enter your password');
      return;
    }

    setIsDeletingAccount(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { password: deletePassword }
      });

      toast.success('Account deleted successfully');
      localStorage.removeItem('token');
      localStorage.removeItem('facilitator');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
      setIsDeletingAccount(false);
    }
  };

  const handleWordInputKeyDown = (e) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      const trimmedInput = currentWordInput.trim();
      if (trimmedInput && enteredWords.length < targetWords.length) {
        setEnteredWords([...enteredWords, trimmedInput]);
        setCurrentWordInput('');
      }
    } else if (e.key === 'Backspace' && !currentWordInput && enteredWords.length > 0) {
      e.preventDefault();
      const newEnteredWords = [...enteredWords];
      newEnteredWords.pop();
      setEnteredWords(newEnteredWords);
    }
  };

  const handleSearchUser = async (e) => {
    e.preventDefault();
    if (!searchEmail) return;

    setSearching(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/search-user?email=${searchEmail}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchedUser(response.data);
      toast.success('User found!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'User not found');
      setSearchedUser(null);
    } finally {
      setSearching(false);
    }
  };

  const handleTogglePaidStatus = async () => {
    if (!searchedUser) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/toggle-paid-status`,
        {
          email: searchedUser.email,
          is_paid: !searchedUser.is_paid
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const newStatus = !searchedUser.is_paid;
      setSearchedUser({ ...searchedUser, is_paid: newStatus });
      
      // If toggling own account, refresh profile immediately
      if (searchedUser.email === profile.email) {
        await fetchProfile();
        toast.success(`Your account has been ${newStatus ? 'upgraded to Premium' : 'downgraded to Free'}! All features updated.`);
      } else {
        toast.success(
          `User ${searchedUser.email} has been ${newStatus ? 'upgraded to Premium' : 'downgraded to Free'}! They should refresh their page or navigate to see changes.`
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const handleToggleAdminStatus = async () => {
    if (!searchedUser || profile.email !== 'etzrdev@gmail.com') return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/toggle-admin-status`,
        {
          email: searchedUser.email,
          is_admin: !searchedUser.is_admin
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setSearchedUser({ ...searchedUser, is_admin: !searchedUser.is_admin });
      toast.success(`User ${searchedUser.is_admin ? 'removed from' : 'added to'} admin!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update admin status');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex justify-between items-center mb-4">
          <PollarBrandMini />
        </div>

        <div className="flex justify-between items-center mb-6">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Back to Dashboard
          </Button>
          
          <div className="flex gap-2">
            {profile?.is_admin && (
              <Button
                onClick={() => window.open('/analytics', '_blank')}
                variant="outline"
                className="border-indigo-300 text-indigo-600 hover:bg-indigo-50"
              >
                📊 Analytics
              </Button>
            )}
            
            <Button
              onClick={() => {
                fetchProfile();
                toast.success('Profile refreshed!');
              }}
              variant="outline"
              className="border-purple-300 text-purple-600 hover:bg-purple-50"
            >
              🔄 Refresh Profile
            </Button>
          </div>
        </div>

        <h1 className="text-4xl font-bold mb-8" style={{ color: '#1a1a1a' }}>Profile Settings</h1>

        <div className="grid gap-6">
          {/* Account Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Account Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-600">Email</Label>
                <p className="text-lg font-medium">{profile.email}</p>
              </div>

              <div>
                <Label className="text-gray-600">Email Verification Status</Label>
                <div className="flex items-center gap-2 mt-1">
                  {profile.email_verified ? (
                    <>
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="text-green-600 font-medium">Verified</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-5 w-5 text-orange-600" />
                      <span className="text-orange-600 font-medium">Not Verified</span>
                    </>
                  )}
                </div>
                {!profile.email_verified && (
                  <Button
                    onClick={handleResendVerification}
                    disabled={resendingVerification}
                    size="sm"
                    variant="outline"
                    className="mt-3 border-orange-300 text-orange-600 hover:bg-orange-50"
                  >
                    {resendingVerification ? 'Sending...' : '📧 Resend Verification Email'}
                  </Button>
                )}
              </div>

              <div>
                <Label className="text-gray-600">Subscription Status</Label>
                <div className="flex flex-col gap-2 mt-2">
                  <div className="flex items-center gap-3">
                    {profile.is_paid ? (
                      <>
                        <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-2 text-base">
                          <Crown className="h-4 w-4 mr-1" />
                          Premium
                        </Badge>
                        
                        {profile.subscription_status === 'lifetime' ? (
                          <span className="text-green-600 font-bold flex items-center gap-1 bg-green-50 px-3 py-1.5 rounded-md border border-green-200">
                            <CheckCircle className="h-4 w-4" />
                            Lifetime Access
                          </span>
                        ) : profile.cancel_at_period_end || profile.subscription_status === 'canceling' ? (
                          <Button
                            onClick={handleReactivateSubscription}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            Reactivate Subscription
                          </Button>
                        ) : (
                          <Button
                            onClick={handleCancelSubscription}
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                          >
                            Cancel Subscription
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        <Badge variant="outline" className="px-4 py-2">Free</Badge>
                        <Button
                          onClick={() => navigate('/subscription')}
                          size="sm"
                          className="bg-gradient-to-r from-purple-600 to-indigo-600"
                        >
                          Upgrade to Premium
                        </Button>
                      </>
                    )}
                  </div>
                  
                  {/* Show end date if canceling */}
                  {(profile.cancel_at_period_end || profile.subscription_status === 'canceling') && profile.current_period_end && (
                    <p className="text-sm text-orange-600 font-medium">
                      Premium status active until {new Date(profile.current_period_end).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-gray-600">Sessions Created</Label>
                <p className="text-lg font-medium">
                  {profile.sessions_created_count}
                  {!profile.is_paid && <span className="text-gray-500"> / 10</span>}
                  {profile.is_paid && <span className="text-green-600 ml-2">(Unlimited ✨)</span>}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Change Password - Hide for Google users */}
          {!profile?.is_google_user && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  Change Password
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div>
                    <Label htmlFor="current_password">Current Password</Label>
                    <Input
                      id="current_password"
                      type="password"
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="new_password">New Password</Label>
                    <Input
                      id="new_password"
                      type="password"
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                      required
                      className="mt-1"
                      placeholder="At least 6 characters"
                    />
                  </div>

                  <div>
                    <Label htmlFor="confirm_password">Confirm New Password</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={changingPassword}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                  >
                    {changingPassword ? 'Changing Password...' : 'Change Password'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Delete Account Section */}
          <Card className="border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <Trash2 className="h-5 w-5" />
                Danger Zone
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-red-900">Delete Account</h3>
                  <p className="text-sm text-red-700 mt-1">
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </p>
                </div>
                <Button 
                  variant="destructive" 
                  onClick={openDeleteModal}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete Account
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Admin Panel - Only visible to admins */}
          {(profile.is_admin || profile.email === 'etzrdev@gmail.com') && (
            <Card className="border-2 border-purple-500">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50">
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-purple-600" />
                  Admin Panel
                  {profile.email === 'etzrdev@gmail.com' && (
                    <Badge className="bg-yellow-500 ml-2">Super Admin</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                {/* Search User */}
                <div>
                  <Label className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Search className="h-5 w-5" />
                    Search & Manage Users
                  </Label>
                  
                  <form onSubmit={handleSearchUser} className="flex gap-2 mt-3">
                    <Input
                      type="email"
                      placeholder="Enter user email..."
                      value={searchEmail}
                      onChange={(e) => setSearchEmail(e.target.value)}
                      required
                      className="flex-1"
                    />
                    <Button
                      type="submit"
                      disabled={searching}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {searching ? 'Searching...' : 'Search'}
                    </Button>
                  </form>
                </div>

                {/* Searched User Details */}
                {searchedUser && (
                  <Card className="bg-gray-50">
                    <CardContent className="pt-6 space-y-4">
                      <div className="flex items-center gap-3 pb-3 border-b">
                        <User className="h-6 w-6 text-gray-600" />
                        <div className="flex-1">
                          <p className="font-medium text-lg">{searchedUser.email}</p>
                          <p className="text-sm text-gray-600">User ID: {searchedUser.id}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-gray-600 text-sm">Email Verified</Label>
                          <p className="font-medium">
                            {searchedUser.email_verified ? '✓ Yes' : '✗ No'}
                          </p>
                        </div>
                        <div>
                          <Label className="text-gray-600 text-sm">Sessions Created</Label>
                          <p className="font-medium">{searchedUser.sessions_created_count}</p>
                        </div>
                      </div>

                      {/* Toggle Paid Status */}
                      <div className="flex items-center justify-between p-4 bg-white rounded-lg">
                        <div>
                          <Label className="font-semibold">Subscription Status</Label>
                          <p className="text-sm text-gray-600">
                            Current: {searchedUser.is_paid ? 'Premium' : 'Free'}
                          </p>
                        </div>
                        <Button
                          onClick={handleTogglePaidStatus}
                          className={`${
                            searchedUser.is_paid
                              ? 'bg-gray-600 hover:bg-gray-700'
                              : 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600'
                          }`}
                        >
                          {searchedUser.is_paid ? 'Downgrade to Free' : 'Upgrade to Premium'}
                        </Button>
                      </div>

                      {/* Toggle Admin Status - Only for Super Admin */}
                      {profile.email === 'etzrdev@gmail.com' && searchedUser.email !== 'etzrdev@gmail.com' && (
                        <div className="flex items-center justify-between p-4 bg-white rounded-lg border-2 border-purple-200">
                          <div>
                            <Label className="font-semibold flex items-center gap-2">
                              <Shield className="h-4 w-4" />
                              Admin Status
                            </Label>
                            <p className="text-sm text-gray-600">
                              Current: {searchedUser.is_admin ? 'Admin' : 'Regular User'}
                            </p>
                          </div>
                          <Button
                            onClick={handleToggleAdminStatus}
                            className={`${
                              searchedUser.is_admin
                                ? 'bg-red-600 hover:bg-red-700'
                                : 'bg-purple-600 hover:bg-purple-700'
                            }`}
                          >
                            {searchedUser.is_admin ? 'Remove Admin' : 'Make Admin'}
                          </Button>
                        </div>
                      )}

                      <div className="pt-4 border-t">
                        <p className="text-xs text-gray-600 text-center mb-2">
                          ⚠️ Changes take effect immediately in database
                        </p>
                        <p className="text-xs text-orange-600 text-center font-medium">
                          Note: User must logout and login again to see updated privileges
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      <Dialog open={isDeleting} onOpenChange={setIsDeleting}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Account
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-2">
              <p>This action is <strong>irreversible</strong>. All your data will be permanently deleted immediately:</p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>Your account and profile details</li>
                <li>All created sessions and messages</li>
                <li>All participant data and responses</li>
                <li>Your active subscription (cancelled immediately)</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Type the following words to confirm:</Label>
              <div className="flex flex-wrap gap-2 p-3 bg-gray-100 rounded-md justify-center">
                {targetWords.map((word, index) => (
                  <Badge key={index} variant="secondary" className="font-mono text-sm px-2 py-1">
                    {word}
                  </Badge>
                ))}
              </div>
              
              <div 
                className="border rounded-md p-2 bg-white focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 cursor-text"
                onClick={() => inputRef.current?.focus()}
              >
                <div className="flex flex-wrap gap-2 items-center">
                  {enteredWords.map((word, index) => (
                    <Badge 
                      key={index} 
                      variant={word === targetWords[index] ? "default" : "destructive"}
                      className="font-mono text-sm"
                    >
                      {word}
                    </Badge>
                  ))}
                  <input
                    ref={inputRef}
                    value={currentWordInput}
                    onChange={(e) => setCurrentWordInput(e.target.value)}
                    onKeyDown={handleWordInputKeyDown}
                    placeholder={enteredWords.length === 0 ? "Type words and press Space/Enter..." : ""}
                    className={`outline-none bg-transparent font-mono text-sm h-8 ${
                      enteredWords.length === targetWords.length 
                        ? "w-[1px] min-w-0 p-0 opacity-0" 
                        : "flex-1 min-w-[120px]"
                    }`}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Type each word and press Space or Enter. Use Backspace to remove.
              </p>
            </div>
            
            {!profile?.is_google_user && (
              <div className="space-y-2">
                <Label>Enter your password:</Label>
                <Input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Your password"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleting(false)} disabled={isDeletingAccount}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteAccount}
              disabled={
                enteredWords.join(' ') !== targetWords.join(' ') || 
                (!profile?.is_google_user && !deletePassword) || 
                isDeletingAccount
              }
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeletingAccount ? 'Deleting...' : 'Permanently Delete Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profile;
